import React from 'react';
import {
  PDFDownloadLink,
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { Button } from '@mui/material';
import logo from '../assets/reserve-logo.png';
import monogram from '../assets/reserve-monogram.png'; // light opacity watermark

const styles = StyleSheet.create({
  page: {
    position: 'relative',
    padding: 20,
    fontFamily: 'Times-Roman',
    fontSize: 10,
    color: 'black',
    backgroundColor: '#FFF8F0',
  },
  watermark: {
    position: 'absolute',
    top: 0, left: 0, width: '100%', height: '100%', opacity: 0.05,
  },
  header: { alignItems: 'center', marginBottom: 12 },
  stripe: { width: '100%', height: 6, backgroundColor: 'black', borderRadius: 3, marginBottom: 8 },
  logo: { width: 125, height: 37.5, objectFit: 'contain', marginBottom: 6 },
  title: { fontSize: 16, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4, color: 'black' },
  section: { marginBottom: 8 },
  orderInfoText: { marginBottom: 2, letterSpacing: 0.5 },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 0.8,
    borderBottomColor: '#8A5A2B',
    paddingBottom: 4,
    marginBottom: 4,
  },
  row: { flexDirection: 'row', paddingVertical: 3 },
  colItem: { width: '50%', textTransform: 'capitalize' },
  colQty: { width: '20%', textAlign: 'center' },
  colSubtotal: { width: '30%', textAlign: 'right' },
  separator: { borderBottomWidth: 0.5, borderBottomColor: '#D4AF37', borderBottomStyle: 'dashed', marginVertical: 6 },
  totals: { flexDirection: 'row', justifyContent: 'space-between', fontSize: 12, fontWeight: 'bold', marginTop: 6, letterSpacing: 1 },
  paymentText: { letterSpacing: 0.5 },
});

function formatDate(ts) {
  const d = ts ? new Date(ts) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getPaymentDisplay(order, piDetails) {
  // Prefer what you saved on the order, then fall back to the PI object.
  const savedPM = order?.paymentMethod;

  const latestCharge = piDetails?.latest_charge || (piDetails?.charges?.data?.[0] ?? undefined);
  const pmd = latestCharge?.payment_method_details;

  const inferredType =
    savedPM?.type ||
    piDetails?.payment_method?.type ||
    pmd?.type ||
    (Array.isArray(piDetails?.payment_method_types) ? piDetails.payment_method_types[0] : undefined);

  if (inferredType === 'card') {
    // Card details from order if present, otherwise from PI (payment_method or latest_charge)
    const cardFromOrder = savedPM?.type === 'card' ? savedPM : undefined;
    const cardFromPI = piDetails?.payment_method?.card || pmd?.card || {};
    const brand = (cardFromOrder?.brand || cardFromPI?.brand || 'Card').toUpperCase();
    const last4 = cardFromOrder?.last4 || cardFromPI?.last4 || '';
    const expMonth = cardFromOrder?.expMonth || cardFromPI?.exp_month || '';
    const expYear = cardFromOrder?.expYear || cardFromPI?.exp_year || '';

    const line1 = `Payment: ${brand}${last4 ? ` ••••${last4}` : ''}`;
    const line2 = expMonth && expYear ? `Exp: ${String(expMonth).padStart(2, '0')}/${expYear}` : null;
    return { type: 'card', line1, line2 };
  }

  if (inferredType === 'paypal') {
    const payerEmail =
      savedPM?.payerEmail ||
      pmd?.paypal?.payer_email ||
      latestCharge?.billing_details?.email ||
      piDetails?.receipt_email ||
      order?.email ||
      null;
    const line1 = `Payment: PayPal${payerEmail ? ` (${payerEmail})` : ''}`;
    return { type: 'paypal', line1, line2: null };
  }

  // Generic fallback
  const line1 = `Payment: ${inferredType ? inferredType.toUpperCase() : '—'}`;
  return { type: inferredType || 'unknown', line1, line2: null };
}

const ReceiptDocument = ({ order, piDetails }) => {
  const shipping = Number(order?.shippingCost || 0);
  const processingFee = typeof order?.processingFee === 'number' ? Number(order.processingFee) : null;
  const total = Number(order?.total || 0);
  const when = formatDate(order?.createdAt);

  const pay = getPaymentDisplay(order, piDetails);

  return (
    <Document>
      <Page size={[216, 612]} style={styles.page}>
        {/* watermark */}
        <Image src={monogram} style={styles.watermark} />

        {/* header with stripe */}
        <View style={styles.stripe} />
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <Text style={styles.title}>Receipt</Text>
        </View>

        {/* order info */}
        <View style={styles.section}>
          <Text style={styles.orderInfoText}>Order ID: <Text>{order?.id || '—'}</Text></Text>
          <Text style={styles.orderInfoText}>Date: <Text>{when}</Text></Text>
        </View>

        <View style={styles.separator} />

        {/* table header */}
        <View style={styles.tableHeader}>
          <Text style={styles.colItem}>Item</Text>
          <Text style={styles.colQty}>Qty</Text>
          <Text style={styles.colSubtotal}>Subtotal (€)</Text>
        </View>

        {/* items */}
        {(order?.items || []).map((item, idx) => {
          const qty = Number(item?.qty || 0);
          const subtotal = Number(item?.subtotal || 0);
          return (
            <View style={styles.row} key={idx}>
              <Text style={styles.colItem}>{item?.name || '—'}</Text>
              <Text style={styles.colQty}>{qty}</Text>
              <Text style={styles.colSubtotal}>€{subtotal.toFixed(2)}</Text>
            </View>
          );
        })}

        <View style={styles.separator} />

        {/* totals */}
        <View style={styles.section}>
          <View style={styles.row}>
            <Text style={styles.colItem}>Shipping</Text>
            <Text style={styles.colSubtotal}>€{shipping.toFixed(2)}</Text>
          </View>
          {typeof processingFee === 'number' && (
            <View style={styles.row}>
              <Text style={styles.colItem}>Processing Fee</Text>
              <Text style={styles.colSubtotal}>€{processingFee.toFixed(2)}</Text>
            </View>
          )}
          <View style={styles.totals}>
            <Text>Total Paid</Text>
            <Text>€{total.toFixed(2)}</Text>
          </View>
        </View>

        <View style={styles.separator} />

        {/* payment method (card OR PayPal OR generic) */}
        <View style={styles.section}>
          <Text style={styles.paymentText}>{pay.line1}</Text>
          {pay.line2 ? <Text style={styles.paymentText}>{pay.line2}</Text> : null}
        </View>
      </Page>
    </Document>
  );
};

export default function ShopperReceipt({ order, piDetails }) {
  return (
    <PDFDownloadLink
      document={<ReceiptDocument order={order} piDetails={piDetails} />}
      fileName={`Marrakech Reserve - Receipt (${order?.id || 'order'}).pdf`}
      style={{ width: '100%', textDecoration: 'none' }}
    >
      {({ loading }) => (
        <Button
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            backgroundColor: '#8A5A2B',
            color: '#FFF8F0',
            fontWeight: 600,
            letterSpacing: 1,
          }}
        >
          {loading ? 'Crafting Luxury…' : 'Download Receipt'}
        </Button>
      )}
    </PDFDownloadLink>
  );
}
